import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { copy } from "../data/copy";
import { byCategory } from "../data/products";

// Colorful "shelf tiles" — each category gets its own warm background.
const TILES = [
    {
        cat: "veg",
        bg: "linear-gradient(140deg, #A3B565 0%, #6A8D2F 100%)",
        img: "/img/cat_veg.jpg",
        rotate: -1.5,
    },
    {
        cat: "nonveg",
        bg: "linear-gradient(140deg, #E37155 0%, #A83E29 100%)",
        img: "/img/cat_nonveg.jpg",
        rotate: 1.5,
    },
    {
        cat: "gongura",
        bg: "linear-gradient(140deg, #C8553D 0%, #6A8D2F 100%)",
        img: "/img/cat_gongura.jpg",
        rotate: -1,
    },
    {
        cat: "sweet",
        bg: "linear-gradient(140deg, #F0C468 0%, #C99B24 100%)",
        img: "/img/cat_sweet.jpg",
        rotate: 1,
    },
];

export const CategoryTiles = () => {
    const { t, lang } = useLang();
    return (
        <section
            className="py-20 md:py-28 bg-[color:var(--kp-cream)]"
            data-testid="category-tiles"
        >
            <div className="max-w-[1300px] mx-auto px-6 md:px-10">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
                    <div>
                        <div className="eyebrow mb-4" data-testid="cat-tiles-eyebrow">
                            {t(copy.categoryTiles.eyebrow)}
                        </div>
                        <h2
                            className={`text-5xl md:text-6xl leading-[0.98] font-bold tracking-editorial ${
                                lang === "te" ? "font-telugu-display" : "font-display"
                            }`}
                        >
                            {t({ en: "Four shelves,", te: "నాలుగు అరలు," })}
                            <br />
                            <em className="italic text-[color:var(--kp-terracotta)]">
                                {t({ en: "one kitchen.", te: "ఒకే వంటగది." })}
                            </em>
                        </h2>
                    </div>
                    <p className={`max-w-md text-[color:var(--kp-ink-soft)] ${lang === "te" ? "font-telugu-body" : "font-body"}`}>
                        {t({
                            en: "Pick a shelf. Every pouch in it comes from the same recipes Kumari Amma has been cooking by hand since 2000.",
                            te: "ఒక అర ఎంచుకోండి. అన్ని ప్యాకెట్లు 2000 నుండి కుమారి అమ్మ రాసిన వంటకాలతోనే.",
                        })}
                    </p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                    {TILES.map((tile, i) => {
                        const count = byCategory(tile.cat).length;
                        const label = copy.categoryTiles[tile.cat];
                        return (
                            <motion.div
                                key={tile.cat}
                                initial={{ opacity: 0, y: 30, rotate: 0 }}
                                whileInView={{ opacity: 1, y: 0, rotate: tile.rotate }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.85, delay: i * 0.09, ease: [0.19, 1, 0.22, 1] }}
                                whileHover={{ rotate: 0, y: -6 }}
                            >
                                <Link
                                    to={`/category/${tile.cat}`}
                                    className="block card-warm relative"
                                    style={{
                                        background: tile.bg,
                                        color: "var(--kp-cream)",
                                        borderRadius: 24,
                                        padding: 16,
                                    }}
                                    data-testid={`category-tile-${tile.cat}`}
                                >
                                    <div className="round-frame aspect-[4/3] mb-3 md:mb-4">
                                        <img
                                            src={tile.img}
                                            alt={t(label.title)}
                                            className="absolute inset-0 w-full h-full object-cover"
                                        />
                                    </div>
                                    <div className="flex items-end justify-between">
                                        <div className="min-w-0">
                                            <div className="text-[10px] tracking-mono-wide opacity-80 mb-1">
                                                {count} {t({ en: "packs", te: "ప్యాకెట్లు" })}
                                            </div>
                                            <h3
                                                className={`text-2xl md:text-3xl leading-none font-bold tracking-editorial truncate ${
                                                    lang === "te" ? "font-telugu-display" : "font-display"
                                                }`}
                                            >
                                                {t(label.title)}
                                            </h3>
                                        </div>
                                        <div className="w-9 h-9 rounded-full bg-[color:var(--kp-cream)] text-[color:var(--kp-ink)] flex items-center justify-center shrink-0 ml-2">
                                            <ArrowUpRight size={14} />
                                        </div>
                                    </div>
                                    <p className={`mt-2 text-[12px] md:text-[13px] opacity-90 line-clamp-2 ${lang === "te" ? "font-telugu-body" : "font-body"}`}>
                                        {t(label.body)}
                                    </p>
                                </Link>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};
